import * as React from 'react';
import { observer } from 'mobx-react';
import { PagePaymentMethodViewModel } from './page-payment-method-view-model';
import { PagePaymentMethodSupportedDescription } from './page-payment-method-supported-description';
import { PayerOnboardingUserAction } from '../../payer-onboarding-user-actions';
import { SubmitButton } from '../../../WebGiving/components/submit-button/submit-button';
import { Form } from '../../../WebGiving/primitives/form';
import { ErrorMessage } from '../../components/error-message/error-message';
import { CardPaymentMethod } from './card-payment-method';
import { Tabs } from '../../../WebGiving/components/tabs/tabs';
import { ResetPageReason, PaymentMethodType } from '../../payer-onboarding-generated';
import { AchPaymentMethod } from './ach-payment-method';
import { FadeTransition } from '../../../WebGiving/components/fade-transition';
import { PanelContentTransition } from '../../../WebGiving/components/panel-content-transition';
import { getPageClasses} from '../helpers/get-page-classes';
import { PanelHeading } from '../../../WebGiving/components/panel-heading';
import { PanelFooter } from '../../../WebGiving/components/panel-footer';
import { Button, ButtonStyle } from '../../../WebGiving/primitives/button/button';
import { StandardTooltip, TooltipPlacement } from '../../../WebGiving/components/tooltip/tooltip';

import * as styles from './page-payment-method.less';
import { pageFooter } from '../../../WebGiving/main/webgiving-main.less';

export { PagePaymentMethodViewModel };

@observer
export class PagePaymentMethod extends React.Component<{
	vm: PagePaymentMethodViewModel,
	raiseAction: (action: PayerOnboardingUserAction) => void,
	transitionState: string,
}> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const {
			error,
			paymentMethods,
			paymentMethodType,
			switchPaymentMethodType,
			merchant,
			achModel,
			cardModel,
			merchantSupportsCard,
			isAchSupported,
			isLoading,
			pageShouldBeVisible,
			isBlocked,
		} = vm;
		const isCard = paymentMethodType === PaymentMethodType.CreditCard;
		const isAch = paymentMethodType === PaymentMethodType.ACH;
		const transitionProps = {
			exit: false,
			timeout: 0,
			duration: parseInt(styles.transitionDurationMs, 10),
		};
		const multiplePaymentMethods = paymentMethods.length > 1;
		const elementsShouldEnter = transitionState === 'entered';

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading)} data-pp-at-target={pageShouldBeVisible ? `Payment Method page` : null}>
				<PanelHeading vm={vm} className={`${styles.panelHeading} ${multiplePaymentMethods ? styles.panelHeadingWithTabs : ''}`}>
					<FadeTransition in={elementsShouldEnter}>
						<div className={styles.panelHeadingContent}>
							<h1 className="panel-title">PAYMENT METHOD (optional)</h1>
							<div className={styles.paymentMethodInfo}>
								<StandardTooltip
									placement={TooltipPlacement.Bottom}
									mobileHeader="Why add a payment method?"
									content={`Adding a payment method means you can make your ${merchant.PaymentLabel.NounPluralLowerCase} faster next time.`}
									tooltipContentId="paymentMethodTooltip">
								</StandardTooltip>
								<p className={styles.paymentMethodInfoText}>Why add a payment method?</p>
							</div>
							{ !merchant.IsDefaultMerchant && <p className="panel-subtitle">
								<PagePaymentMethodSupportedDescription merchant={merchant} supportsCard={merchantSupportsCard} />
							</p>}
							{ multiplePaymentMethods && (
								<div className={styles.tabs}>
									<Tabs options={paymentMethods} selectedTab={paymentMethodType} onChange={switchPaymentMethodType} />
								</div>
							)}
						</div>
					</FadeTransition>
				</PanelHeading>
				<Form onSubmit={this.handleSubmit}>
					<div className="panel-body panel-body-form" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage error={error} onResetPage={this.handleResetPage}/>
								{ merchantSupportsCard && (
									<FadeTransition in={isCard} {...transitionProps}>
										<div>
											<CardPaymentMethod vm={cardModel} />
										</div>
									</FadeTransition>
								)}
								{ isAchSupported && (
									<FadeTransition in={isAch} {...transitionProps}>
										<div>
											<AchPaymentMethod vm={achModel} />
										</div>
									</FadeTransition>
								)}
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={pageFooter}>
						<div className="row">
							<div className="col-sm-6">
								<SubmitButton acceptanceTestTargetId="Next" isLoading={isLoading} isBlocked={isBlocked}>Next</SubmitButton>
							</div>
							<div className={`col-sm-6 ${styles.colSkipBtn}`}>
								<Button className="button"
									buttonStyle={ButtonStyle.Ghost}
									acceptanceTestTargetId="Skip"
									onClick={this.handleSkip}
									isBlocked={isLoading}>
									Skip
								</Button>
							</div>
						</div>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		this.props.raiseAction(new PayerOnboardingUserAction.SubmitCurrentPage());
	}

	private handleSkip = () => {
		this.props.raiseAction(new PayerOnboardingUserAction.SkipPaymentMethod());
	}

	private handleResetPage = (reason?: ResetPageReason) => {
		this.props.raiseAction(new PayerOnboardingUserAction.ResetPageBecauseOfError(reason));
	}
}
